import { confirmInstallmentPosting } from "./bank-posting";
import { cardPaymentEventId } from "./card-payments";
import type { FinancialSnapshot, Movement } from "./types";
import { addMonthsClamped } from "./installments";

export interface BankPostingInput {
  id: string;
  confirmed: boolean;
  expectedAmountCents: number;
  expectedPeriod: string;
  expectedRevision: number;
  expectedPostingState: string;
  expectedPostingRevision?: number;
  scheduledOn?: string;
  postedOn?: string;
  repeatMonthly?: boolean;
}

/** Sólo metadatos y copia rápida; no reconoce gastos ni modifica dinero. */
export async function saveBankPosting(uid: string, input: BankPostingInput, operationId: string) {
  if (!/^bankposting_[a-zA-Z0-9-]+$/.test(operationId) || !input.id || input.id.includes("/")
    || typeof input.confirmed !== "boolean") throw new Error("Confirmación inválida.");
  if (input.scheduledOn && !addMonthsClamped(input.scheduledOn, 0)) throw new Error("La fecha de cargo indicada no existe.");
  if (input.repeatMonthly && !input.scheduledOn) throw new Error("Indica la fecha antes de repetir el calendario.");
  const [{ doc, runTransaction, getDocFromServer, getDocsFromServer, collection, query, where }, { firestore }] = await Promise.all([import("firebase/firestore"), import("./firebase")]);
  const ref = (name: string, id: string) => doc(firestore, "users", uid, name, id);
  const target = await getDocFromServer(ref("movements", input.id));
  if (!target.exists()) throw new Error("No encontré la parcialidad.");
  const planId = target.data().installmentPurchaseId;
  if (typeof planId !== "string") throw new Error("No encontré el plan MSI.");
  const discovered = input.repeatMonthly ? await getDocsFromServer(query(collection(firestore, "users", uid, "movements"), where("installmentPurchaseId", "==", planId))) : undefined;
  const ids = discovered ? discovered.docs.map(row => row.id) : [input.id];
  const requestKey = JSON.stringify(input);
  return runTransaction(firestore, async tx => {
    const operation = await tx.get(ref("operations", operationId));
    const state = await tx.get(ref("snapshots", "current"));
    const rows = [];
    for (const id of ids) rows.push(await tx.get(ref("movements", id)));
    const row = rows.find(item => item.id === input.id);
    if (!state.exists() || !row?.exists() || rows.some(item => !item.exists())) throw new Error("Actualiza los movimientos antes de confirmar.");
    const snapshot = state.data() as FinancialSnapshot;
    const movement = row.data() as Movement;
    if (operation.exists()) {
      if (operation.data().requestKey !== requestKey) throw new Error("El identificador corresponde a otra confirmación.");
      return { snapshot, result: { ok: true, alreadyApplied: true, movimientosActualizados: rows.map(item => item.data() as Movement) } };
    }
    if ((movement.billedAmountCents ?? movement.amountCents) !== input.expectedAmountCents
      || (movement.cardPeriod ?? "") !== input.expectedPeriod
      || (movement.correctionRevision ?? 0) !== input.expectedRevision
      || (movement.bankPostingRevision ?? 0) !== (input.expectedPostingRevision ?? 0)
      || (movement.bankPostingState ?? "") !== input.expectedPostingState) {
      throw new Error("La parcialidad cambió. Actualiza y revisa nuevamente.");
    }
    const now = new Date().toISOString();
    const updates = rows.map(item => item.data() as Movement).filter(item => item.id === input.id
      || (input.repeatMonthly && item.installmentPurchaseId === planId
        && Number(item.installmentNumber) > Number(movement.installmentNumber)
        && item.cardPaymentStatus !== "paid" && !item.historicalSettlement && item.bankPostingState !== "confirmed"));
    const paymentIds = [...new Set(updates.filter(item => item.cardPeriod).map(item => cardPaymentEventId(item.paymentMethod, item.cardPeriod!)))];
    for (const id of paymentIds) {
      if ((await tx.get(ref("cardPaymentEvents", id))).exists()) throw new Error("El corte pagado conserva su historial.");
    }
    const updated = updates.map(item => {
      const scheduledOn = input.scheduledOn ? addMonthsClamped(input.scheduledOn, Number(item.installmentNumber) - Number(movement.installmentNumber)) : undefined;
      const bounds = item.cardPeriod?.match(/^(\d{4}-\d{2}-\d{2}) al (\d{4}-\d{2}-\d{2})$/);
      if (scheduledOn && bounds && (scheduledOn < bounds[1] || scheduledOn > bounds[2])) {
        throw new Error("La fecha indicada cae fuera del corte asignado. Revisa el corte antes de guardar el calendario.");
      }
      return confirmInstallmentPosting(item, item.id === input.id ? input.confirmed : false, now, scheduledOn, item.id === input.id ? input.postedOn : undefined);
    });
    const next = { ...snapshot, updatedAt: now,
      movements: snapshot.movements.map(item => updated.find(row => row.id === item.id) ?? item) };
    updated.forEach(item => tx.set(ref("movements", item.id), item));
    tx.set(ref("snapshots", "current"), next);
    tx.set(ref("operations", operationId), { id: operationId, kind: "installment_bank_posting",
      createdAt: now, requestKey, input, before: updates, after: updated });
    return { snapshot: next, result: { ok: true, movimientosActualizados: updated } };
  });
}
